// Timeline slot maths and region-local time formatting.
//
// Every clock face the user sees is rendered in the *selected city's*
// timezone, not the browser's. Someone in London looking at Bangkok should see
// Bangkok's evening, so `timeZone` is passed explicitly to every formatter.

import { WallClockStep } from '../types';

/** Must match TIMELINE_* in backend/src/barService.ts. */
export const SLOT_STEP_MINUTES = 30;
export const HORIZON_HOURS = 12;
export const SLOT_COUNT = (HORIZON_HOURS * 60) / SLOT_STEP_MINUTES + 1; // 25
export const MAX_OFFSET_MINUTES = HORIZON_HOURS * 60; // 720

export function clampOffsetMinutes(minutes: number): number {
  if (!Number.isFinite(minutes)) return 0;
  return Math.min(MAX_OFFSET_MINUTES, Math.max(0, minutes));
}

/** Snap an arbitrary minute offset onto the 30-minute grid. */
export function snapOffsetMinutes(minutes: number): number {
  const snapped = Math.round(clampOffsetMinutes(minutes) / SLOT_STEP_MINUTES) * SLOT_STEP_MINUTES;
  return clampOffsetMinutes(snapped);
}

export function offsetToSlotIndex(offsetMinutes: number): number {
  const index = Math.round(clampOffsetMinutes(offsetMinutes) / SLOT_STEP_MINUTES);
  return Math.min(SLOT_COUNT - 1, Math.max(0, index));
}

/**
 * The city wall clock at the selected offset. Clamps to the last supplied step
 * so a short payload degrades to stale pricing rather than crashing.
 */
export function clockStepForOffset(
  clock: WallClockStep[] | undefined,
  offsetMinutes: number
): WallClockStep | null {
  if (!clock || clock.length === 0) return null;
  const index = Math.min(offsetToSlotIndex(offsetMinutes), clock.length - 1);
  return clock[index];
}

const timeFormatterCache = new Map<string, Intl.DateTimeFormat>();

function timeFormatter(timezone: string): Intl.DateTimeFormat {
  let formatter = timeFormatterCache.get(timezone);
  if (!formatter) {
    formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    });
    timeFormatterCache.set(timezone, formatter);
  }
  return formatter;
}

/**
 * Format an instant on a city's clock, e.g. "8:30 PM".
 * An unknown timezone falls back to the browser's rather than throwing.
 */
export function formatTimeInZone(instant: Date | string, timezone: string): string {
  const date = typeof instant === 'string' ? new Date(instant) : instant;
  if (Number.isNaN(date.getTime())) return '';
  try {
    return timeFormatter(timezone).format(date);
  } catch {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    }).format(date);
  }
}

/** "Now", "+ 30 min", "+ 1 hr", "+ 3.5 hr" — mirrors the iOS slider label. */
export function formatOffsetDescriptor(offsetMinutes: number): string {
  const minutes = clampOffsetMinutes(offsetMinutes);
  if (minutes === 0) return 'Now';
  if (minutes < 60) return `+ ${minutes} min`;

  const hours = minutes / 60;
  const label = Number.isInteger(hours) ? String(hours) : hours.toFixed(1);
  return `+ ${label} hr`;
}

/**
 * "ends in 25 min" / "ends at 8:00 PM", measured from the *selected* moment so
 * the copy stays truthful while scrubbing into the future.
 */
export function formatRelativeEnd(
  endUtc: string,
  fromInstant: Date,
  timezone: string
): string {
  const end = new Date(endUtc);
  if (Number.isNaN(end.getTime())) return '';
  const minsLeft = Math.round((end.getTime() - fromInstant.getTime()) / 60_000);
  if (minsLeft <= 0) return 'ending soon';
  if (minsLeft < 60) return `ends in ${minsLeft} min`;
  return `ends at ${formatTimeInZone(end, timezone)}`;
}
